import { call, put, takeEvery, select, take, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  addTest,
  updateTestStatus,
  moveToNextTest,
  addCompletedTestIdToSuite,
  setTestSuiteSessionId,
  setSuiteRunStartedAt,
  setSuiteRunEndedAt,
  clearSessionResultReplacement,
  setAutoPlayCountdown,
  setSkipNextAutoPlay,
  startNextTest,
  setSelectedTest,
  setActiveTest,
  TestSuiteItem,
  startFirstTest,
  restartTestInSuite,
  setTestSuiteSharedConfiguration,
  updateTestConfiguration as updateTestSuiteItemConfiguration,
  buildRestartConfiguration,
  loadTestSuite,
} from "./testSuiteSlice";
import { v4 as uuidv4 } from "uuid";
import { TestConfiguration, TestStatus } from "types/testTypes";
import {
  setTestStatus,
  selectTest,
  updateTestConfiguration as updateTestConfig,
  clearCollectedTestData,
  setTestId,
  testConfigurationUpdateComplete,
  updateTestConfiguration,
  resetTestState,
  setTestCompletedPercentage,
} from "@store/tests/testSlice";
import { resetTestSession } from "@store/testsession/testSessionSlice";
import {
  selectTestSuiteTests,
  selectActiveTestIndex,
  selectAutoPlay,
  selectAutoPlayCountdown,
  selectTestById,
  selectSuiteId,
  selectSuiteName,
  selectTestSuiteSessionId,
  selectReplacesTestSuiteTestId,
  selectSelectedTestIndex,
  selectTestSuiteSharedConfiguration,
  selectSkipNextAutoPlay,
  selectFirstTestCompleted,
  selectCalibrationEnabledTestId,
  selectSuiteRunStartedAt,
} from "./testSuiteSelectors";
import {
  selectTestId,
  selectTestConfiguration,
  selectAvailablePreTestConfigurationOptions,
  selectTestProgressConfiguration,
  selectAvailableProgressConfigurationOptions,
} from "@store/tests/testSelectors";
import { selectSelectedPatient } from "@store/patients/patientSelectors";
import { waitForState } from "@utils/sagaUtils";
import {
  createTestSuiteSession,
  updateTestSuiteSession,
  addTestSuiteSessionResult,
} from "@utils/api-communication/RestApiCli";

function* handleAddTest(_action: PayloadAction<{ id?: string; configuration: TestConfiguration }>) {
  const tests: TestSuiteItem[] = yield select(selectTestSuiteTests);
  const newTest = tests[tests.length - 1];

  if (newTest) {
    yield put(setTestId(newTest.id));
  }
}

function* handleUpdateTestStatus(
  _action: PayloadAction<{ id: string; status: TestSuiteItem["status"]; progress?: number }>,
) {}

function* handleStartNext() {
  const tests = yield select(selectTestSuiteTests);
  const activeTestIndex = yield select(selectActiveTestIndex);
  const firstTestCompleted: boolean = yield select(selectFirstTestCompleted);
  const calibrationEnabledTestId: string | null = yield select(selectCalibrationEnabledTestId);

  if (activeTestIndex === null || tests.length === 0) {
    return;
  }

  yield put(moveToNextTest());

  const updatedTests = yield select(selectTestSuiteTests);
  const nextActiveIndex = yield select(selectActiveTestIndex);

  if (nextActiveIndex !== null && nextActiveIndex < updatedTests.length) {
    const nextTest = updatedTests[nextActiveIndex];
    const patient = yield select(selectSelectedPatient);

    if (patient && nextTest) {
      yield put(setSelectedTest(nextActiveIndex));

      yield put(setTestStatus({ status: TestStatus.TestConfiguration }));
      yield put(clearCollectedTestData());

      yield take(setTestId.type);

      if (nextTest.configuration.name) {
        yield put(selectTest(nextTest.configuration.name));

        yield take(testConfigurationUpdateComplete.type);

        const config: TestConfiguration | undefined = yield call(
          waitForState,
          selectTestConfiguration,
          (config: TestConfiguration | null) => config?.name === nextTest.configuration.name,
          2000,
        );

        if (!config) {
          return;
        }
      }

      // Merge shared configuration (e.g., Virtual Assistant settings) from suite level
      const sharedConfig: Partial<TestConfiguration> | null = yield select(
        selectTestSuiteSharedConfiguration,
      );
      const nextVirtualAssistanceConfig = nextTest.configuration.virtualAssistanceConfig ?? {};
      const sharedVirtualAssistanceConfig = sharedConfig?.virtualAssistanceConfig ?? {};
      const configToApply = {
        ...buildRestartConfiguration(
          nextTest.configuration,
          firstTestCompleted,
          nextTest.id === calibrationEnabledTestId,
        ),
        virtualAssistanceConfig: {
          ...nextVirtualAssistanceConfig,
          ...sharedVirtualAssistanceConfig,
        },
      };

      yield put(updateTestConfig(configToApply));

      yield take(testConfigurationUpdateComplete.type);

      yield put(updateTestStatus({ id: nextTest.id, status: "loading", progress: 0 }));

      yield put(setTestStatus({ status: TestStatus.StartRequested }));
    }
  }
}

export function* handleTestStatusChange(
  action: PayloadAction<{ status: TestStatus; extraData?: any }>,
) {
  const status = action.payload.status;
  const extraData = action.payload.extraData;
  const tests = yield select(selectTestSuiteTests);
  const activeTestIndex = yield select(selectActiveTestIndex);

  if (activeTestIndex === null || tests.length === 0) {
    return;
  }

  // TerminateRequested records the operator's intent, not headset
  // acknowledgement. In particular, STOP_TEST is refused while calibration
  // owns the scene. Start the next suite item only after SCENE_SET_MENU has
  // confirmed the pending termination by returning the test flow to
  // TestConfiguration.
  if (status === TestStatus.TestConfiguration) {
    const currentTest = tests[activeTestIndex];
    if (currentTest?.status !== "terminated") {
      return;
    }

    const autoPlay = yield select(selectAutoPlay);
    const autoPlayCountdown: number | null = yield select(selectAutoPlayCountdown);
    const skipNextAutoPlay = yield select(selectSkipNextAutoPlay);
    if (autoPlay && autoPlayCountdown == null && !skipNextAutoPlay) {
      yield put(setAutoPlayCountdown(3));
    }
    if (skipNextAutoPlay) {
      yield put(setSkipNextAutoPlay(false));
    }
    return;
  }

  if (status === TestStatus.WaitingForNextEye) {
    const currentTest = tests[activeTestIndex];
    if (currentTest) {
      const firstEyeTestId = yield select(selectTestId);
      if (firstEyeTestId && currentTest.id) {
        yield put(
          addCompletedTestIdToSuite({
            testSuiteTestId: currentTest.id,
            completedTestResultId: firstEyeTestId,
          }),
        );
      }
    }
    return;
  }

  if (
    status === TestStatus.Completed ||
    status === TestStatus.TerminateRequested ||
    status === TestStatus.Error
  ) {
    const currentTest = tests[activeTestIndex];

    if (currentTest) {
      if (
        status === TestStatus.Completed &&
        (currentTest.status === "not started" || currentTest.status === "completed")
      ) {
        return;
      }
      if (status === TestStatus.Completed) {
        const completedTestResultId = yield select(selectTestId);
        if (completedTestResultId && currentTest.id) {
          const existingTestResultId = currentTest.completedTestResultId;
          const isDuplicateCompletion = !!(
            existingTestResultId &&
            existingTestResultId.split(",").some((id) => id.trim() === completedTestResultId)
          );

          if (!isDuplicateCompletion) {
            let finalTestResultId = completedTestResultId;

            if (existingTestResultId) {
              const expectedSecondId = existingTestResultId + "2";
              if (completedTestResultId === expectedSecondId) {
                finalTestResultId = `${existingTestResultId},${completedTestResultId}`;
              } else {
                console.warn(
                  `Back-to-back test second ID mismatch. Expected: ${expectedSecondId}, Got: ${completedTestResultId}. ` +
                    `Existing ID(s): ${existingTestResultId}. Replacing instead of appending.`,
                );
                finalTestResultId = completedTestResultId;
              }
            }

            yield put(
              addCompletedTestIdToSuite({
                testSuiteTestId: currentTest.id,
                completedTestResultId: finalTestResultId,
              }),
            );

            if (
              currentTest.configuration?.name != null &&
              currentTest.configuration?.testType != null
            ) {
              let sessionId: string | null = yield select(selectTestSuiteSessionId);
              if (!sessionId) {
                const suiteId: string | null = yield select(selectSuiteId);
                const suiteName: string | null = yield select(selectSuiteName);
                const patient = yield select(selectSelectedPatient);
                const suiteStartedAt: number | null = yield select(selectSuiteRunStartedAt);
                if (suiteId && suiteName) {
                  const newSessionId = uuidv4();
                  try {
                    yield call(
                      () =>
                        new Promise<void>((resolve, reject) => {
                          createTestSuiteSession(
                            {
                              sessionId: newSessionId,
                              testSuiteId: suiteId,
                              testSuiteName: suiteName,
                              patientId: patient?.uuid,
                              suiteStartedAt: suiteStartedAt ?? Date.now(),
                            },
                            () => resolve(),
                            reject,
                          );
                        }),
                    );
                    // Only publish the sessionId to Redux after the backend
                    // confirms the row exists, so other readers (e.g. results
                    // navigation, follow-up PATCHes) never see a phantom id.
                    yield put(setTestSuiteSessionId(newSessionId));
                    sessionId = newSessionId;
                  } catch {
                    console.error("Failed to start test suite session");
                    sessionId = null;
                  }
                }
              }

              if (sessionId) {
                const replacesTestSuiteTestId: string | undefined = yield select(
                  selectReplacesTestSuiteTestId,
                  currentTest.id,
                );
                const testSuiteTestIdForSession = replacesTestSuiteTestId ?? currentTest.id;
                try {
                  yield call(
                    () =>
                      new Promise<void>((resolve, reject) => {
                        addTestSuiteSessionResult(
                          sessionId,
                          {
                            testSuiteTestId: testSuiteTestIdForSession,
                            completedTestResultId: finalTestResultId,
                            testName: currentTest.configuration.name,
                            testType: String(currentTest.configuration.testType),
                          },
                          () => resolve(),
                          reject,
                        );
                      }),
                  );
                  if (replacesTestSuiteTestId) {
                    yield put(clearSessionResultReplacement(currentTest.id));
                  }
                } catch {
                  console.error("Failed to add test result to session");
                }
              }
            }
          }
        }
      }

      const isRestart = extraData?.isRestart === true;

      if (!isRestart || status !== TestStatus.TerminateRequested) {
        let newStatus: TestSuiteItem["status"];
        if (status === TestStatus.Completed) {
          newStatus = "completed";
        } else if (status === TestStatus.TerminateRequested) {
          newStatus = "terminated";
        } else {
          newStatus = "error";
        }
        yield put(
          updateTestStatus({
            id: currentTest.id,
            status: newStatus,
            progress: status === TestStatus.Completed ? 100 : currentTest.progress || 0,
          }),
        );
        const testsAfterUpdate: TestSuiteItem[] = yield select(selectTestSuiteTests);
        const terminalStatuses = ["completed", "terminated", "skipped", "error"];
        const allTestsDone = testsAfterUpdate.every((t) => terminalStatuses.includes(t.status));
        if (allTestsDone) {
          yield put(
            setSuiteRunEndedAt({
              endedAt: Date.now(),
              status: status === TestStatus.Completed ? "completed" : "terminated",
            }),
          );
        }
      }

      const autoPlay = yield select(selectAutoPlay);
      const skipNextAutoPlay = yield select(selectSkipNextAutoPlay);
      if (
        status !== TestStatus.TerminateRequested &&
        status !== TestStatus.Error &&
        autoPlay &&
        !isRestart &&
        !skipNextAutoPlay
      ) {
        yield put(setAutoPlayCountdown(3));
      }
      if (status !== TestStatus.TerminateRequested && skipNextAutoPlay) {
        yield put(setSkipNextAutoPlay(false));
      }
    }
  }
}

function* handleStartNextTest() {
  yield call(handleStartNext);
}

function* handleStartFirstTest(action: PayloadAction<{ testIndex: number }>) {
  const { testIndex } = action.payload;
  const tests: TestSuiteItem[] = yield select(selectTestSuiteTests);
  const patient = yield select(selectSelectedPatient);
  const firstTestCompleted: boolean = yield select(selectFirstTestCompleted);

  if (!patient || tests.length === 0 || testIndex >= tests.length) {
    return;
  }

  const firstTest = tests[testIndex];
  if (!firstTest) {
    return;
  }

  yield put(setSelectedTest(testIndex));

  if (firstTest.configuration.name) {
    yield put(selectTest(firstTest.configuration.name));
    yield take(testConfigurationUpdateComplete.type);

    const config: TestConfiguration | undefined = yield call(
      waitForState,
      selectTestConfiguration,
      (config: TestConfiguration | null) => config?.name === firstTest.configuration.name,
      2000,
    );

    if (!config) {
      return;
    }
  }

  // Merge shared configuration (e.g., Virtual Assistant settings) from suite level
  const sharedConfig: Partial<TestConfiguration> | null = yield select(
    selectTestSuiteSharedConfiguration,
  );
  const configToApply = sharedConfig
    ? {
        ...buildRestartConfiguration(firstTest.configuration, firstTestCompleted),
        virtualAssistanceConfig: {
          ...firstTest.configuration.virtualAssistanceConfig,
          ...sharedConfig.virtualAssistanceConfig,
        },
      }
    : buildRestartConfiguration(firstTest.configuration, firstTestCompleted);

  yield put(updateTestConfig(configToApply));

  yield take(testConfigurationUpdateComplete.type);

  yield put(updateTestStatus({ id: firstTest.id, status: "loading", progress: 0 }));

  const suiteId: string | null = yield select(selectSuiteId);
  const suiteName: string | null = yield select(selectSuiteName);
  if (suiteId && suiteName) {
    // Clear any sessionId left over from a previous run on this slice instance
    // so the next test completion lazily creates a fresh backend session.
    yield put(setTestSuiteSessionId(null));
    yield put(setSuiteRunStartedAt(Date.now()));
    yield put(setSuiteRunEndedAt(null));
  }

  yield put(setTestStatus({ status: TestStatus.StartRequested }));
}

function* handleSetSuiteRunEndedAt(
  action: PayloadAction<{ endedAt: number; status: "completed" | "terminated" } | null>,
) {
  const payload = action.payload;
  if (payload == null) return;
  const { endedAt: suiteEndedAt, status } = payload;
  const sessionId: string | null = yield select(selectTestSuiteSessionId);
  if (!sessionId) return;
  try {
    yield call(
      () =>
        new Promise<void>((resolve, reject) => {
          updateTestSuiteSession(sessionId, { suiteEndedAt, status }, () => resolve(), reject);
        }),
    );
  } catch {
    console.error("Failed to update test suite session end time");
  }
}

export function* handleUpdateTestSuiteItemAfterMutation(
  _action: PayloadAction<{ id: string; configuration: Partial<TestConfiguration> }>,
) {
  const testSuiteId: string | null = yield select(selectTestId);

  if (!testSuiteId) {
    return;
  }

  const configuration: TestConfiguration = yield select(selectTestConfiguration);
  const availableConfigurations = yield select(selectAvailablePreTestConfigurationOptions);
  const progressConfiguration = yield select(selectTestProgressConfiguration);
  const availableProgressConfigurations = yield select(selectAvailableProgressConfigurationOptions);

  const relevantTestSuiteItem: TestSuiteItem = yield select(selectTestById(testSuiteId));
  if (relevantTestSuiteItem) {
    yield put(
      updateTestSuiteItemConfiguration({
        id: testSuiteId,
        testSuiteItem: {
          ...relevantTestSuiteItem,
          availableConfigurations,
          availableProgressConfigurations,
          progressConfiguration,
          configuration: {
            ...configuration,
            // suite slice owns this flag; the test slice copy is stale
            startWithCalibrationScene:
              relevantTestSuiteItem.configuration.startWithCalibrationScene,
          },
        },
      }),
    );
  }
}

function* handleRestartTestInSuite(
  action: PayloadAction<{
    testToRemoveId: string;
    testToRestart: {
      id: string;
      configuration: TestConfiguration;
      completedTestResultId?: string;
    };
    testIndex: number;
    skipNextAutoPlay?: boolean;
  }>,
) {
  const { testToRestart, testIndex, skipNextAutoPlay } = action.payload;

  if (skipNextAutoPlay) {
    yield put(setAutoPlayCountdown(null));
  }

  yield put(setActiveTest(testIndex));
  yield put(setSelectedTest(testIndex));

  yield call(() => new Promise((resolve) => setTimeout(resolve, 50)));

  yield put(updateTestStatus({ id: testToRestart.id, status: "loading", progress: 0 }));

  yield put(resetTestState());
  yield put(setTestCompletedPercentage(0));
  yield put(resetTestSession());

  const currentSelectedTest: string | null = yield select(
    (state) => state.test.configuration?.name,
  );
  const currentEventCodes = yield select((state) => state.test.testEventCodes);

  if (
    testToRestart.configuration.name &&
    (currentSelectedTest !== testToRestart.configuration.name || !currentEventCodes)
  ) {
    yield put(selectTest(testToRestart.configuration.name));

    yield take(testConfigurationUpdateComplete.type);

    const config: TestConfiguration | undefined = yield call(
      waitForState,
      selectTestConfiguration,
      (config: TestConfiguration | null) => config?.name === testToRestart.configuration.name,
      2000,
    );

    if (!config) {
      console.error(
        `Failed to wait for test configuration in restartTestInSuite for test: ${testToRestart.configuration.name} (id: ${testToRestart.id})`,
      );
      return;
    }
  }

  const firstTestCompleted: boolean = yield select(selectFirstTestCompleted);
  yield put(
    updateTestConfiguration(
      buildRestartConfiguration(testToRestart.configuration, firstTestCompleted),
    ),
  );

  yield take(testConfigurationUpdateComplete.type);

  const testId = uuidv4();
  yield put(setTestId(testId));

  yield put(updateTestStatus({ id: testToRestart.id, status: "loading", progress: 0 }));

  yield put(setTestStatus({ status: TestStatus.StartRequested }));
}

function* handleLoadTestSuite(
  action: PayloadAction<{
    suiteId: string;
    suiteName: string;
    tests: TestSuiteItem[];
    activeTestIndex: number | null;
    selectedTestIndex?: number | null;
    completedTestIds?: string[];
    autoPlay?: boolean;
    restoreFirstTestConfiguration?: boolean;
  }>,
) {
  if (action.payload.restoreFirstTestConfiguration === false) return;

  const tests: TestSuiteItem[] = yield select(selectTestSuiteTests);
  if (!tests.length) return;

  const selectedTestIndex: number | null = yield select(selectSelectedTestIndex);
  const activeTestIndex: number | null = yield select(selectActiveTestIndex);
  const initialIndex = selectedTestIndex ?? activeTestIndex ?? 0;
  const targetIndex = initialIndex >= 0 && initialIndex < tests.length ? initialIndex : 0;
  const targetTest = tests[targetIndex];
  const targetTestName = targetTest?.configuration?.name;
  if (!targetTestName) return;

  yield put(setTestId(targetTest.id));
  yield put(selectTest(targetTestName));
  yield take(testConfigurationUpdateComplete.type);
  const settledConfig: TestConfiguration | undefined = yield call(
    waitForState,
    selectTestConfiguration,
    (config: TestConfiguration | null) => config?.name === targetTestName,
    3000,
  );
  if (!settledConfig) return;

  yield put(updateTestConfig(targetTest.configuration));
  yield take(testConfigurationUpdateComplete.type);

  const resolvedAvailableConfigurations: TestSuiteItem["availableConfigurations"] = yield select(
    selectAvailablePreTestConfigurationOptions,
  );
  const resolvedAvailableProgressConfigurations: TestSuiteItem["availableProgressConfigurations"] =
    yield select(selectAvailableProgressConfigurationOptions);
  const latestSuiteItem: TestSuiteItem | null = yield select(selectTestById(targetTest.id));
  if (latestSuiteItem) {
    yield put(
      updateTestSuiteItemConfiguration({
        id: targetTest.id,
        testSuiteItem: {
          ...latestSuiteItem,
          availableConfigurations: resolvedAvailableConfigurations,
          availableProgressConfigurations: resolvedAvailableProgressConfigurations,
        },
      }),
    );
  }

  yield put(setSelectedTest(targetIndex));
}

function* handleSetTestSuiteSharedConfiguration(
  action: PayloadAction<Partial<TestConfiguration> | null>,
) {
  const sharedConfig = action.payload;
  const tests: TestSuiteItem[] = yield select(selectTestSuiteTests);
  const activeTestIndex: number | null = yield select(selectActiveTestIndex);
  const selectedTestIndex: number | null = yield select(selectSelectedTestIndex);
  const currentTestConfiguration: TestConfiguration | null = yield select(selectTestConfiguration);

  if (!sharedConfig || tests.length === 0) {
    return;
  }

  for (let i = 0; i < tests.length; i++) {
    if (i === activeTestIndex) {
      continue;
    }

    const test = tests[i];
    const updatedConfiguration = {
      ...test.configuration,
      virtualAssistanceConfig: {
        ...test.configuration.virtualAssistanceConfig,
        ...sharedConfig.virtualAssistanceConfig,
      },
    };

    yield put(
      updateTestSuiteItemConfiguration({
        id: test.id,
        testSuiteItem: {
          ...test,
          configuration: updatedConfiguration,
        },
      }),
    );
  }

  if (
    selectedTestIndex !== null &&
    selectedTestIndex !== activeTestIndex &&
    currentTestConfiguration
  ) {
    const updatedTestConfig = {
      ...currentTestConfiguration,
      virtualAssistanceConfig: {
        ...currentTestConfiguration.virtualAssistanceConfig,
        ...sharedConfig.virtualAssistanceConfig,
      },
    };
    yield put(updateTestConfig(updatedTestConfig));
  }
}

export default function* testSuiteSaga() {
  yield takeEvery(addTest.type, handleAddTest);
  yield takeEvery(updateTestStatus.type, handleUpdateTestStatus);
  yield takeEvery(setTestStatus.type, handleTestStatusChange);
  yield takeEvery(setSuiteRunEndedAt.type, handleSetSuiteRunEndedAt);
  yield takeEvery(startNextTest.type, handleStartNextTest);
  yield takeEvery(startFirstTest.type, handleStartFirstTest);
  yield takeEvery(restartTestInSuite.type, handleRestartTestInSuite);
  yield takeLatest(testConfigurationUpdateComplete.type, handleUpdateTestSuiteItemAfterMutation);
  yield takeLatest(setTestSuiteSharedConfiguration.type, handleSetTestSuiteSharedConfiguration);
  yield takeLatest(loadTestSuite.type, handleLoadTestSuite);
}
